// Utility functions for managing domain-specific images

// Global tracking for image attempts per domain
let currentDomainId = null;
let imageTrackingData = {};

/**
 * Clear image tracking data when domain changes
 * @param {string} domainId - The new domain ID
 */
export const setCurrentDomain = (domainId) => {
  if (currentDomainId !== domainId) {
    currentDomainId = domainId;
    imageTrackingData = {};
  }
};

/**
 * Get the base path for domain-specific images
 * @param {string} domainId - The domain ID (e.g., 'geotab-camera')
 * @returns {string} - The base path for domain images
 */
export const getDomainImagePath = (domainId) => {
  return `/assets/domains/${domainId}`;
};

/**
 * Get a specific image path for a domain
 * @param {string} domainId - The domain ID
 * @param {string} imageName - The image filename
 * @returns {string} - The full image path
 */
export const getDomainImage = (domainId, imageName) => {
  return `${getDomainImagePath(domainId)}/${imageName}`;
};

/**
 * Get the first available image with a given base name and supported extensions
 * @param {string} domainId - The domain ID
 * @param {string} baseName - The base name (e.g., 'hero-main', 'hero-secondary')
 * @param {Array} extensions - Array of supported extensions (default: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
 * @returns {string} - The full image path of the first found image
 */
export const getDomainImageAuto = (domainId, baseName, extensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']) => {
  // Common extensions in order of preference
  const commonExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
  const validExtensions = extensions.filter(ext => commonExtensions.includes(ext.toLowerCase()));
  
  // Return the first extension - the error handler will try others if this fails
  if (validExtensions.length > 0) {
    return `${getDomainImagePath(domainId)}/${baseName}.${validExtensions[0]}`;
  }
  
  // Fallback to jpg if no valid extensions
  return `${getDomainImagePath(domainId)}/${baseName}.jpg`;
};

/**
 * Get all possible image paths for a given base name
 * @param {string} domainId - The domain ID
 * @param {string} baseName - The base name (e.g., 'hero-main', 'hero-secondary')
 * @param {Array} extensions - Array of supported extensions
 * @returns {Array} - Array of all possible image paths
 */
export const getAllPossibleImagePaths = (domainId, baseName, extensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']) => {
  const commonExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
  const validExtensions = extensions.filter(ext => commonExtensions.includes(ext.toLowerCase()));
  
  return validExtensions.map(ext => `${getDomainImagePath(domainId)}/${baseName}.${ext}`);
};

/**
 * Simple error handler that tries a few common extensions and then falls back
 * @param {Event} event - The error event
 * @param {string} domainId - The domain ID
 * @param {string} baseName - The base name (e.g., 'hero-main', 'hero-secondary')
 */
export const handleImageErrorWithFallback = (event, domainId, baseName) => {
  // Ensure we're tracking the current domain
  setCurrentDomain(domainId);
  
  // Get the current path and extract the extension
  const currentPath = event.target.src;
  const currentExt = currentPath.split('.').pop().toLowerCase();
  
  // Create a unique key for this domain and base name combination
  const trackingKey = `${domainId}_${baseName}`;
  
  // Get or initialize tracking data for this image
  if (!imageTrackingData[trackingKey]) {
    imageTrackingData[trackingKey] = [currentExt];
  } else {
    imageTrackingData[trackingKey].push(currentExt);
  }
  
  // Define all possible extensions to try
  const allExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
  
  // Find next extension to try
  const nextExt = allExtensions.find(ext => !imageTrackingData[trackingKey].includes(ext));
  
  if (nextExt) {
    // Try next extension
    const nextPath = `${getDomainImagePath(domainId)}/${baseName}.${nextExt}`;
    event.target.src = nextPath;
    return;
  }
  
  // If all extensions failed, use external fallback
  const fallbackImages = getFallbackImages(domainId);
  event.target.src = fallbackImages[0];
  event.target.onerror = null; // Prevent infinite loop
};


/**
 * Get fallback images based on domain type
 * @param {string} domainId - The domain ID
 * @returns {Array} - Array of fallback image URLs
 */
export const getFallbackImages = (domainId) => {
  const fallbackImages = {
    // Camera domains

    'geotab-dashcams': [
      'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'geotab-camera': [
      'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'geotab-dashcam': [
      'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'fleet-dashcam': [
      'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    // Store domains
    'geotabstore': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'geotab-equipment': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    // Keyless domains
    'fleet-keyless': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'geotab-key': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'keyless-fleet': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'geotab-keyless': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    // Tracking domains
    'smarter-tracking': [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'go-insights': [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'road-eazy': [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    // Other domains
    'fleet-hours-of-service': [
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556742111-a301076d9d18?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ],
    'fleet-flix': [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
    ]
  };

  return fallbackImages[domainId] || fallbackImages['smarter-tracking'];
};

/**
 * Get thumbnail fallback images (smaller versions)
 * @param {string} domainId - The domain ID
 * @returns {Array} - Array of thumbnail fallback image URLs
 */
export const getThumbnailFallbackImages = (domainId) => {
  const fallbackImages = getFallbackImages(domainId);
  return fallbackImages.map(url => url.replace('w=1200', 'w=300'));
};

/**
 * Handle image loading error and provide fallback
 * @param {Event} event - The error event
 * @param {string} domainId - The domain ID
 * @param {number} index - The image index for fallback
 */
export const handleImageError = (event, domainId, index = 0) => {
  const fallbackImages = getFallbackImages(domainId);
  event.target.src = fallbackImages[index % fallbackImages.length];
  event.target.onerror = null; // Prevent infinite loop
};

/**
 * Handle thumbnail image loading error and provide fallback
 * @param {Event} event - The error event
 * @param {string} domainId - The domain ID
 * @param {number} index - The image index for fallback
 */
export const handleThumbnailError = (event, domainId, index = 0) => {
  const fallbackImages = getThumbnailFallbackImages(domainId);
  event.target.src = fallbackImages[index % fallbackImages.length];
  event.target.onerror = null; // Prevent infinite loop
};
